import { useState, useEffect, useRef } from "react";
import { IdCard, Upload, Loader2, Trash2, Eye, Save, X, Users, ScanLine, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/pages/Residents";
import SmartScanner from "@/components/scanner/SmartScanner";
import { buildPdf } from "@/lib/scanner/paper";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
// (dialog popup diganti dengan halaman penuh — tidak perlu impor Dialog lagi)
import { GENDERS, RELIGIONS, MARITAL, BLOOD, EDUCATION, RELATIONSHIP, CITIZENSHIP, OCCUPATION } from "@/constants/refData";

const EMPTY_MEMBER = {
  no: 0, full_name: "", nik: "", gender: "", birth_place: "", birth_date: "", religion: "",
  education: "", occupation: "", blood_type: "", marital_status: "", marriage_date: "",
  relationship: "", citizenship: "WNI", passport_no: "", kitap_no: "", father_name: "", mother_name: "",
};

async function dataUrlToFile(dataUrl, filename) {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], filename, { type: blob.type || "image/jpeg" });
}

// Muat gambar (File/dataUrl) menjadi { dataUrl, width, height } untuk diarsipkan sebagai PDF.
function loadImagePage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ dataUrl, width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error("gagal memuat gambar"));
    img.src = dataUrl;
  });
}
async function fileToPage(file) {
  const dataUrl = await new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result);
    fr.onerror = () => rej(fr.error);
    fr.readAsDataURL(file);
  });
  return loadImagePage(dataUrl);
}

export default function KartuKeluarga() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [smartOpen, setSmartOpen] = useState(false);
  const fileRef = useRef(null);
  const scanPageRef = useRef(null); // gambar hasil scan (hasil rapi) utk diarsipkan saat Simpan

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/kk"); setCards(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doScan = async (file, page = null) => {
    if (!file) return;
    setScanning(true);
    toast.message("Memindai dokumen KK... (10-40 detik)");
    // Simpan gambar hasil scan (hasil rapi) untuk diarsipkan nanti saat tombol Simpan ditekan.
    try { scanPageRef.current = page || await fileToPage(file); }
    catch { scanPageRef.current = null; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/kk/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const data = r.data;
      data.members = (data.members || []).map((m, i) => ({ ...EMPTY_MEMBER, ...m, no: m.no || i + 1 }));
      setPreview(data);
      toast.success(`Berhasil memindai ${data.members.length} anggota keluarga`);
    } catch (e) { toast.error(apiError(e)); } finally { setScanning(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    doScan(file);
  };

  // Hasil Smart Scanner (sudah dikoreksi perspektif + dijernihkan) -> baca AI vision
  const onSmartComplete = async (scanPages) => {
    setSmartOpen(false);
    const pg = scanPages?.[0];
    if (!pg?.dataUrl) return;
    try {
      const file = await dataUrlToFile(pg.dataUrl, `kk-scan-${Date.now()}.png`);
      doScan(file, pg); // pg = hasil rapi (dataUrl PNG + dimensi) utk arsip
    } catch (e) {
      toast.error("Gagal memproses hasil pindai");
    }
  };

  const setF = (k) => (v) => setPreview((p) => ({ ...p, [k]: v }));
  const setMember = (idx, k, v) =>
    setPreview((p) => ({ ...p, members: p.members.map((m, i) => (i === idx ? { ...m, [k]: v } : m)) }));
  const addMember = () =>
    setPreview((p) => ({ ...p, members: [...p.members, { ...EMPTY_MEMBER, no: p.members.length + 1 }] }));
  const removeMember = (idx) =>
    setPreview((p) => ({ ...p, members: p.members.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!preview.kk_number) { toast.error("Nomor KK wajib diisi"); return; }
    setSaving(true);
    try {
      const r = await api.post("/kk", preview);
      toast.success(`Kartu Keluarga tersimpan. ${r.data.members_saved} warga masuk ke Data Warga.`);
      // Arsipkan salinan KK (hasil rapi) ke "Dokumen Saya" sebagai PDF LOSSLESS.
      const pg = scanPageRef.current;
      if (pg?.dataUrl) {
        try {
          const orient = pg.width >= pg.height ? "landscape" : "portrait";
          const blob = buildPdf([pg], "A4", orient);
          const fname = `KK ${preview.kk_number}.pdf`;
          const fd = new FormData();
          fd.append("file", new File([blob], fname, { type: "application/pdf" }));
          fd.append("document_type", "kk");
          fd.append("paper_size", "A4");
          fd.append("orientation", orient);
          fd.append("pages", "1");
          fd.append("original_filename", fname);
          await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
          toast.success("Salinan KK diarsipkan di Dokumen Saya");
        } catch (_) { /* jangan gagalkan penyimpanan warga bila arsip gagal */ }
      }
      scanPageRef.current = null;
      setPreview(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/kk/${id}`); toast.success("Kartu Keluarga dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // Layar PERIKSA DATA HASIL PINDAI — halaman penuh (bukan popup), responsif semua perangkat.
  if (preview) {
    return (
      <div className="max-w-6xl mx-auto pb-28">
        {/* Bar aksi atas (sticky) */}
        <div className="sticky top-0 z-20 -mx-4 sm:mx-0 bg-white/95 backdrop-blur border-b border-slate-200 px-4 sm:px-0 sm:pb-3">
          <div className="flex items-center gap-3 py-3">
            <Button variant="ghost" size="sm" onClick={() => setPreview(null)} className="rounded-lg shrink-0 px-2">
              <ArrowLeft size={18} className="mr-1" /> <span className="hidden sm:inline">Kembali</span>
            </Button>
            <div className="min-w-0 flex-1">
              <h1 className="font-heading text-base sm:text-xl font-bold text-slate-900 flex items-center gap-2 truncate">
                <Eye size={18} className="shrink-0 text-[#064E3B]" /> Periksa Data Hasil Pindai
              </h1>
              <p className="text-[11px] sm:text-xs text-slate-500 truncate">Periksa &amp; koreksi setiap kolom sebelum menyimpan.</p>
            </div>
            <Button onClick={save} disabled={saving} data-testid="kk-save-button" className="bg-[#064E3B] hover:bg-[#043E2F] rounded-xl shrink-0 hidden sm:inline-flex">
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />} Simpan
            </Button>
          </div>
        </div>

        <div className="space-y-5 pt-5">
          {/* Data tingkat KK */}
          <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5">
            <h2 className="font-heading font-semibold text-slate-900 mb-3 text-sm sm:text-base">Data Kartu Keluarga</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <PF label="No. KK" value={preview.kk_number} onChange={setF("kk_number")} tid="kk-f-number" />
              <PF label="Kepala Keluarga" value={preview.head_name} onChange={setF("head_name")} tid="kk-f-head" />
              <PF label="Alamat" value={preview.address} onChange={setF("address")} tid="kk-f-address" />
              <div className="grid grid-cols-2 gap-2">
                <PF label="RT" value={preview.rt} onChange={setF("rt")} tid="kk-f-rt" />
                <PF label="RW" value={preview.rw} onChange={setF("rw")} tid="kk-f-rw" />
              </div>
              <PF label="Desa/Kelurahan" value={preview.village_name} onChange={setF("village_name")} tid="kk-f-village" />
              <PF label="Kecamatan" value={preview.district_name} onChange={setF("district_name")} tid="kk-f-district" />
              <PF label="Kabupaten/Kota" value={preview.regency_name} onChange={setF("regency_name")} tid="kk-f-regency" />
              <PF label="Provinsi" value={preview.province_name} onChange={setF("province_name")} tid="kk-f-province" />
              <PF label="Kode Pos" value={preview.postal_code} onChange={setF("postal_code")} tid="kk-f-postal" />
              <PF label="Tgl Dikeluarkan" value={preview.issued_date} onChange={setF("issued_date")} tid="kk-f-issued" />
              <PF label="Nama Pejabat" value={preview.official_name} onChange={setF("official_name")} tid="kk-f-official" />
              <PF label="NIP Pejabat" value={preview.official_nip} onChange={setF("official_nip")} tid="kk-f-nip" />
            </div>
            <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-500 flex items-start gap-2 break-all">
              <span className="font-semibold text-slate-600 shrink-0">Isi QR:</span> {preview.qr_content || "(tidak terbaca — QR akan dibuat dari No. KK)"}
            </div>
          </div>

          {/* Anggota keluarga */}
          <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5">
            <div className="flex items-center justify-between gap-2 mb-2">
              <h2 className="font-heading font-semibold text-slate-900 text-sm sm:text-base">Anggota Keluarga ({preview.members.length})</h2>
              <Button size="sm" variant="outline" onClick={addMember} data-testid="kk-add-member" className="rounded-lg shrink-0">+ Tambah</Button>
            </div>
            <p className="text-xs text-slate-500 mb-3">Geser tabel ke samping untuk melihat semua kolom. Kolom <span className="px-1 rounded bg-amber-50 border border-amber-300 text-amber-700">kuning</span> masih kosong. NIK <span className="px-1 rounded bg-red-50 border border-red-400 text-red-600">merah</span> = kemungkinan salah baca (tanggal lahir pada NIK tak cocok) — mohon periksa &amp; perbaiki.</p>
            <div className="overflow-x-auto custom-scroll border border-slate-200 rounded-xl">
              <table className="w-full text-xs" data-testid="kk-members-table">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-left">
                    {["No","Nama Lengkap","NIK","L/P","Tempat Lahir","Tgl Lahir","Agama","Pendidikan","Pekerjaan","Gol. Darah","Status Kawin","Tgl Kawin","Hubungan","Kewarganegaraan","No. Paspor","No. KITAP","Ayah","Ibu",""].map((h) => (
                      <th key={h} className="px-2 py-2 font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.members.map((m, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`kk-member-row-${i}`}>
                      <td className="px-2 py-1 text-center">{i + 1}</td>
                      <MCell v={m.full_name} on={(v) => setMember(i, "full_name", v)} w="w-40" />
                      <MCell v={m.nik} on={(v) => setMember(i, "nik", v)} w="w-36" warn={m.nik_valid === false} note={m.nik_note} />
                      <MSelect v={m.gender} on={(v) => setMember(i, "gender", v)} opts={GENDERS} w="w-24" />
                      <MCell v={m.birth_place} on={(v) => setMember(i, "birth_place", v)} w="w-28" />
                      <MCell v={m.birth_date} on={(v) => setMember(i, "birth_date", v)} w="w-24" />
                      <MSelect v={m.religion} on={(v) => setMember(i, "religion", v)} opts={RELIGIONS} w="w-28" />
                      <MSelect v={m.education} on={(v) => setMember(i, "education", v)} opts={EDUCATION} w="w-40" />
                      <MSelect v={m.occupation} on={(v) => setMember(i, "occupation", v)} opts={OCCUPATION} w="w-44" />
                      <MSelect v={m.blood_type} on={(v) => setMember(i, "blood_type", v)} opts={BLOOD} w="w-20" />
                      <MSelect v={m.marital_status} on={(v) => setMember(i, "marital_status", v)} opts={MARITAL} w="w-28" />
                      <MCell v={m.marriage_date} on={(v) => setMember(i, "marriage_date", v)} w="w-24" />
                      <MSelect v={m.relationship} on={(v) => setMember(i, "relationship", v)} opts={RELATIONSHIP} w="w-32" />
                      <MSelect v={m.citizenship} on={(v) => setMember(i, "citizenship", v)} opts={CITIZENSHIP} w="w-20" />
                      <MCell v={m.passport_no} on={(v) => setMember(i, "passport_no", v)} w="w-24" />
                      <MCell v={m.kitap_no} on={(v) => setMember(i, "kitap_no", v)} w="w-24" />
                      <MCell v={m.father_name} on={(v) => setMember(i, "father_name", v)} w="w-32" />
                      <MCell v={m.mother_name} on={(v) => setMember(i, "mother_name", v)} w="w-32" />
                      <td className="px-1">
                        <button onClick={() => removeMember(i)} data-testid={`kk-remove-member-${i}`} className="p-1 text-red-500 hover:bg-red-50 rounded"><X size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Bar aksi bawah (sticky) — utama untuk mobile */}
        <div className="fixed bottom-0 inset-x-0 z-20 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-3 flex gap-2"
             style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
          <Button variant="outline" onClick={() => setPreview(null)} className="rounded-xl flex-1 sm:flex-none">Batal</Button>
          <Button onClick={save} disabled={saving} className="bg-[#064E3B] hover:bg-[#043E2F] rounded-xl flex-1 sm:flex-none">
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />} Simpan ke Data Warga
          </Button>
        </div>
      </div>
    );
  }


  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {smartOpen && (
        <SmartScanner singlePage orientation="portrait" onComplete={onSmartComplete} onClose={() => setSmartOpen(false)} />
      )}
      <PageHeader
        icon={IdCard} title="Kartu Keluarga" subtitle="Pindai foto KK untuk mengisi data warga otomatis"
        action={
          <>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} data-testid="kk-file-input" />
            <Button onClick={() => setSmartOpen(true)} disabled={scanning} data-testid="kk-smartscan-button" variant="outline" className="rounded-xl h-11 border-[#064E3B] text-[#064E3B] hover:bg-emerald-50">
              <ScanLine size={18} className="mr-2" /> Scan Rapi
            </Button>
            <Button onClick={() => fileRef.current?.click()} disabled={scanning} data-testid="kk-scan-button" className="bg-[#064E3B] hover:bg-[#043E2F] rounded-xl h-11">
              {scanning ? <Loader2 className="animate-spin mr-2" size={18} /> : <Upload size={18} className="mr-2" />}
              Unggah Foto
            </Button>
          </>
        }
      />

      {/* Info banner */}
      <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-[#064E3B] grid place-items-center shrink-0"><Upload size={18} className="text-white" /></div>
        <div className="text-sm text-slate-600">
          Gunakan <b>Scan Rapi</b> (deteksi tepi otomatis di kamera, auto-foto, koreksi perspektif & penjernihan) atau <b>Unggah Foto</b> Kartu Keluarga yang jelas. Sistem membaca seluruh data anggota keluarga otomatis (AI vision), termasuk isi QR code. Anda dapat memeriksa & mengoreksi data sebelum menyimpan.
        </div>
      </div>

      {loading ? (
        <div className="p-16 grid place-items-center"><Loader2 className="animate-spin text-[#064E3B]" size={28} /></div>
      ) : cards.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
          <div className="h-14 w-14 rounded-2xl bg-emerald-50 grid place-items-center mx-auto mb-4"><IdCard className="text-[#064E3B]" /></div>
          <p className="text-slate-500">Belum ada Kartu Keluarga. Klik tombol Kamera atau Unggah Foto untuk memulai.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="kk-list">
          {cards.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`kk-card-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-slate-400 font-mono">No. KK {c.kk_number}</div>
                  <div className="font-heading text-lg font-bold text-slate-900 mt-0.5">{c.head_name}</div>
                  <div className="text-sm text-slate-500 flex items-center gap-1 mt-1"><Users size={14} /> {c.members?.length || 0} anggota</div>
                  <div className="text-xs text-slate-400 mt-1">{c.village_name}, {c.district_name}, {c.regency_name}</div>
                </div>
                {c.qr_image && <img src={c.qr_image} alt="QR" className="h-16 w-16" />}
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                <Button size="sm" variant="outline" onClick={() => remove(c.id)} data-testid={`kk-delete-${c.id}`} className="rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50">
                  <Trash2 size={15} className="mr-1.5" /> Hapus
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PF({ label, value, onChange, tid }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-500">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} className="h-9 text-sm" />
    </div>
  );
}

function MCell({ v, on, w, warn, note }) {
  const empty = !String(v || "").trim();
  const border = warn ? "border-red-400 bg-red-50" : empty ? "border-amber-300 bg-amber-50" : "border-slate-200";
  return (
    <td className="px-1 py-1 align-top">
      <input
        value={v || ""}
        onChange={(e) => on(e.target.value)}
        title={warn ? note : undefined}
        className={`${w} px-2 py-1 rounded border outline-none text-xs focus:border-[#064E3B] ${border}`}
      />
      {warn && note ? <div className={`${w} mt-0.5 text-[10px] leading-tight text-red-600`}>{note}</div> : null}
    </td>
  );
}

function MSelect({ v, on, opts, w }) {
  const val = String(v || "");
  const empty = !val.trim();
  // Always include the scanned value as an option so nothing is hidden/lost.
  const merged = val && !opts.includes(val) ? [val, ...opts] : opts;
  return (
    <td className="px-1 py-1">
      <select
        value={val}
        onChange={(e) => on(e.target.value)}
        className={`${w} px-2 py-1 rounded border outline-none text-xs bg-white focus:border-[#064E3B] ${empty ? "border-amber-300 bg-amber-50" : "border-slate-200"}`}
      >
        <option value="">— pilih —</option>
        {merged.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </td>
  );
}
